import React, { useEffect, useState } from 'react';
import { Modal, Col, Row } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import CustomButton from '../CustomButton';
import CustomInput from '../CustomInput';
import CustomSelect from '../CustomSelect';
import { saveAffiliateBankAccount } from '../../Services/User/Affiliate';
import { showToast } from '../Toast/toast';
import { applyValidationErrorsToFormik } from '../../Utils/contentModeration';

const accountTypeOptions = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
];

const validationSchema = (isUpdate) =>
  Yup.object({
    account_holder_name: Yup.string().required('Account holder name is required'),
    bank_name: Yup.string().required('Bank name is required'),
    account_number: isUpdate
      ? Yup.string().nullable()
      : Yup.string().required('Account number is required'),
    routing_number: Yup.string().required('Routing number is required'),
    country: Yup.string().length(2, 'Use 2-letter country code').required('Country is required'),
    account_type: Yup.string().oneOf(['checking', 'savings']).required('Account type is required'),
    iban: Yup.string().nullable(),
    swift_code: Yup.string().nullable(),
  });

const AffiliateBankAccountModal = ({ show, onHide, bankAccount, hasBankAccount = false, onSaved }) => {
  const [initialValues, setInitialValues] = useState({
    account_holder_name: '',
    bank_name: '',
    account_number: '',
    routing_number: '',
    iban: '',
    swift_code: '',
    country: 'US',
    account_type: 'checking',
  });

  useEffect(() => {
    if (bankAccount) {
      setInitialValues({
        account_holder_name: bankAccount.account_holder_name || '',
        bank_name: bankAccount.bank_name || '',
        account_number: '',
        routing_number: bankAccount.routing_number || '',
        iban: bankAccount.iban || '',
        swift_code: bankAccount.swift_code || '',
        country: bankAccount.country || 'US',
        account_type: bankAccount.account_type || 'checking',
      });
    } else {
      setInitialValues({
        account_holder_name: '',
        bank_name: '',
        account_number: '',
        routing_number: '',
        iban: '',
        swift_code: '',
        country: 'US',
        account_type: 'checking',
      });
    }
  }, [bankAccount, show]);

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      const payload = {
        ...values,
        iban: values.iban || null,
        swift_code: values.swift_code || null,
      };

      if (!payload.account_number) {
        delete payload.account_number;
      }

      const response = await saveAffiliateBankAccount(payload);
      showToast(response.message || 'Bank account saved successfully', 'success');
      onSaved?.();
      onHide();
    } catch (error) {
      if (error.errors) {
        applyValidationErrorsToFormik(error.errors, setFieldError);
      } else {
        showToast(error.message || 'Failed to save bank account', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Body className="p-4 position-relative">
        <button type="button" className="btn-close position-absolute top-0 end-0 m-3" onClick={onHide} aria-label="Close">
          <FaTimes />
        </button>
        <h4 className="mb-4">{hasBankAccount ? 'Update Bank Account' : 'Add Bank Account'}</h4>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema(hasBankAccount)}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
            <Form>
              <Row>
                <Col xs={12} md={6} className="mb-3">
                  <CustomInput
                    label="Account Holder Name"
                    id="account_holder_name"
                    name="account_holder_name"
                    value={values.account_holder_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.account_holder_name && errors.account_holder_name}
                  />
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <CustomInput
                    label="Bank Name"
                    id="bank_name"
                    name="bank_name"
                    value={values.bank_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.bank_name && errors.bank_name}
                  />
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <CustomInput
                    label="Account Number"
                    id="account_number"
                    name="account_number"
                    value={values.account_number}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={hasBankAccount ? 'Leave blank to keep current account number' : 'Enter account number'}
                    error={touched.account_number && errors.account_number}
                  />
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <CustomInput
                    label="Routing Number"
                    id="routing_number"
                    name="routing_number"
                    value={values.routing_number}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.routing_number && errors.routing_number}
                  />
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <CustomInput
                    label="Country (2-letter code)"
                    id="country"
                    name="country"
                    value={values.country}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={2}
                    error={touched.country && errors.country}
                  />
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <CustomSelect
                    label="Account Type"
                    id="account_type"
                    name="account_type"
                    options={accountTypeOptions}
                    value={values.account_type}
                    onChange={(e) => setFieldValue('account_type', e.target.value)}
                  />
                  {touched.account_type && errors.account_type && (
                    <div className="text-danger small mt-1">{errors.account_type}</div>
                  )}
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <CustomInput
                    label="IBAN (optional)"
                    id="iban"
                    name="iban"
                    value={values.iban}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.iban && errors.iban}
                  />
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <CustomInput
                    label="SWIFT Code (optional)"
                    id="swift_code"
                    name="swift_code"
                    value={values.swift_code}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.swift_code && errors.swift_code}
                  />
                </Col>
              </Row>
              <div className="d-flex gap-2 justify-content-end mt-2">
                <CustomButton type="button" variant="secondary" className="secondaryButton" onClick={onHide}>
                  Cancel
                </CustomButton>
                <CustomButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </CustomButton>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AffiliateBankAccountModal;
